import {
  adminKeys,
  auditKeys,
  companyKeys,
  documentKeys,
  meetingKeys,
  miscKeys,
  presetKeys,
  publicDocumentKeys,
  requestKeys,
  stableFilters,
  templateKeys,
} from '@shared/api/query-keys';

// The canonical query-key
// registry now lives in the hierarchical factories under `@shared/api/query-keys`. EVERY
// member of `QK` below is a thin ALIAS that delegates to a factory — there are no inline key
// definitions left here. `QK` is kept purely for back-compat with the ~348 existing call
// sites; new code should import the factory directly (`documentKeys.detail(...)`), which the
// repo-wide eslint guard nudges you toward. Deleting `QK` entirely is a mechanical follow-up
// (migrate each call site to its factory) — tracked as the optional tail of M8.

export const QK = {
  // Migrated to @shared/api/query-keys (auditKeys) — aliased for back-compat (P4-tail).
  audits: auditKeys.list,
  audit: auditKeys.detail,
  auditStatus: auditKeys.status,
  auditReportStatus: auditKeys.reportStatus,
  auditArchiveStatus: auditKeys.archiveStatus,
  auditRoles: auditKeys.roles,
  auditRole: auditKeys.role,
  myAuditTeam: auditKeys.myTeam,
  auditTeamMembers: auditKeys.teamMembers,
  auditParticipants: auditKeys.participants,
  // Migrated to @shared/api/query-keys (documentKeys) — aliased for back-compat (P3).
  // documentsFiltered / documentsFilteredDeleted removed: dead keys (0 usages).
  document: documentKeys.detail,
  documentEditor: documentKeys.editor,
  documents: documentKeys.list,
  documentsDeleted: documentKeys.deletedList,
  // Migrated to @shared/api/query-keys (meetingKeys) — aliased for back-compat (P4).
  meetingAll: meetingKeys.listRoot,
  userRole: auditKeys.myRole,
  user: miscKeys.user,
  getAllUsers: miscKeys.allUsers,
  signature: miscKeys.signature,
  notifications: miscKeys.notifications,
  auditAuditorFirmMembers: auditKeys.auditorFirmMembers,
  meetings: meetingKeys.list,
  meetingsAll: meetingKeys.allForAudit,
  meeting: meetingKeys.detail,
  requests: (auditId?: string, params?: { statusFilter?: string[]; assigned?: boolean }) => {
    // `statusFilter` is a set (order-insensitive) → sort it so equivalent filters share a
    // cache entry. Structural object (no JSON.stringify) keeps `['requests', auditId]` a
    // true prefix, so prefix-invalidation covers every filtered variant.
    const filters = stableFilters(params, { sortArrays: true });
    return filters ? (['requests', auditId, filters] as const) : (['requests', auditId] as const);
  },
  team: miscKeys.team,
  teamAuditorFirms: miscKeys.teamAuditorFirms,
  // Migrated to @shared/api/query-keys (companyKeys) — aliased for back-compat (P4-tail).
  company: {
    byId: companyKeys.detail,
    commercialRegister: companyKeys.commercialRegister,
  },
  // Migrated to @shared/api/query-keys (presetKeys) — aliased for back-compat.
  presets: {
    base: () => presetKeys.all(),
  },
  // Migrated to @shared/api/query-keys — aliased for back-compat (P4-tail).
  superadmin: {
    aiConfig: adminKeys.aiConfig,
    teams: adminKeys.teams,
    users: adminKeys.users,
    companies: adminKeys.companies,
    company: adminKeys.company,
    vouchers: adminKeys.vouchers,
  },
  // status — migrated to @shared/api/query-keys (statusKeys), fully cut over (P2).
  publicDocument: {
    base: publicDocumentKeys.base,
    attempt: publicDocumentKeys.attempt,
  },

  // ── Request Flow (v2) — migrated to @shared/api/query-keys (requestKeys),
  //    aliased for back-compat (P5a). Backlinks stay a separate root on purpose
  //    (see request-flow.keys.ts). ──────────────────────────────────────────
  requestContainer: requestKeys.container,
  requestContainers: requestKeys.list,
  requestContainerActivity: requestKeys.containerActivity,
  requestContainerBacklinks: requestKeys.backlinks,
  requestBlockActivity: requestKeys.blockActivity,
  requestBlockComments: requestKeys.blockComments,
  requestBlockResponseHistory: requestKeys.blockResponseHistory,
  bexioRefetchStatus: requestKeys.bexioRefetchStatus,
  bexioExportStatus: requestKeys.bexioExportStatus,

  // ── Templates + Example Documents — migrated to @shared/api/query-keys
  //    (templateKeys), aliased for back-compat (P4-tail). ─────────────────────
  requestTemplates: templateKeys.requestList,
  requestTemplatesInstantiable: templateKeys.requestListInstantiable,
  requestTemplate: templateKeys.request,
  requestTemplateVersionActivity: templateKeys.requestVersionActivity,
  blockTemplates: templateKeys.blockList,
  blockTemplate: templateKeys.block,
  blockTemplateVersionActivity: templateKeys.blockVersionActivity,
  exampleDocuments: templateKeys.exampleDocuments,
  requestExampleDocument: templateKeys.requestExampleDocument,

  assertions: miscKeys.assertions,
} as const;

// export type QueryKey = ReturnType<(typeof QK)[keyof typeof QK]>;
